import {
	useState,
	useEffect,
	useRef,
	type ChangeEvent,
} from 'react';
import {
	Box,
	IconButton,
	Chip,
	Switch,
	CircularProgress,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	Checkbox,
	TextField,
	InputAdornment,
	FormControlLabel,
} from '@mui/material';
import { getProfileCommonJss } from './ProfileCommonJss';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import Check from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import LocationService from '../../../services/LocationService';

type LocationLink = {
	client_location_id?: number | string;
	client_locationid?: number | string;
	isOverride?: boolean;
	service_type_code?: string;
	locationclassid?: number | string;
	[key: string]: any;
};

type ClientClass = {
	id: number;
	client_id?: number | string;
	location_links?: LocationLink[];
	[key: string]: any;
};

type LocationItem = {
	id?: number | string;
	client_location_id?: number | string;
	clientid?: number | string;
	client_id?: number | string;
	class_links: LocationLink[];
	location_name?: string;
	name?: string;
	location_code?: string;
	code?: string;
	drop_date?: string | null;
	deleted_at?: string | null;
	[key: string]: any;
};

type ServiceBadgeByLocation = Record<string, { isOverride?: boolean; service?: string }>;

type ClientProfileLocationListProps = {
	locations?: any[];
	onLocationChange: (items: any[]) => void;
	multiMode: boolean;
	onMultiModeChange?: (event: ChangeEvent<HTMLInputElement>) => void;
	onCurrentClassChange?: (clientClass: any) => void;
	currentClasses?: any[];
	assignAvailable?: boolean;
	updateLocations?: (locations: any[]) => void;
	isAdmin?: boolean;
	visualVariant?: string;
};

function ClientProfileLocationList(props: ClientProfileLocationListProps) {
	const {
		locations,
		onLocationChange,
		multiMode,
		onMultiModeChange,
		onCurrentClassChange,
		currentClasses = [],
		assignAvailable = false,
		isAdmin = false,
	} = props;
	const jss = getProfileCommonJss(props?.visualVariant);
	const [choosedLocations, setChoosedLocations] = useState<LocationItem[]>([]);
	const [showDeleted, setShowDeleted] = useState(false);
	const [openFilter, setOpenFilter] = useState(false);
	const [keyword, setKeyword] = useState('');
	const [showAllCheck, setShowAllCheck] = useState(false);
	const [existLocations, setExistLocations] = useState<string[]>([]);
	const [existLocationClasses, setExistLocationClasses] = useState<ServiceBadgeByLocation | null>(null);
	const filterElement = useRef<HTMLButtonElement | null>(null);
	const selectedClassIdRef = useRef<number | string | null>(null);

	const getLocationId = (location: LocationItem | LocationLink | null | undefined): string => {
		if (!location) {
			return '';
		}
		const rawId =
			(location as LocationItem).client_location_id
			?? (location as LocationItem).id
			?? (location as LocationLink).client_locationid;
		return rawId === undefined || rawId === null ? '' : String(rawId);
	};

	const getLocationName = (location: LocationItem): string => {
		return location.location_name || location.name || '';
	};

	const getLocationCode = (location: LocationItem): string => {
		return location.location_code || location.code || '';
	};

	useEffect(() => {
		setChoosedLocations([]);
	}, [multiMode]);

	useEffect(() => {
		onLocationChange(choosedLocations);
	}, [choosedLocations, onLocationChange]);

	useEffect(() => {
		if (currentClasses && currentClasses.length > 0) {
			const currentClassId = currentClasses[0]?.id ?? null;
			const hasClassChanged = currentClassId !== selectedClassIdRef.current;
			selectedClassIdRef.current = currentClassId;

			const exists = new Set<string>();
			const classServicesByLocation: ServiceBadgeByLocation = {};

			currentClasses.forEach((clientClass) => {
				(clientClass.location_links || []).forEach((link: LocationLink) => {
					const locationId = getLocationId(link);
					if (!locationId) {
						return;
					}
					classServicesByLocation[locationId] = {
						isOverride: link.isOverride,
						service: link.service_type_code,
					};
					exists.add(locationId);
				});
			});

			setExistLocationClasses(classServicesByLocation);
			setExistLocations(Array.from(exists));

			if (hasClassChanged) {
				setChoosedLocations([]);
			}
		} else {
			selectedClassIdRef.current = null;
			setExistLocationClasses(null);
			setExistLocations([]);
			setChoosedLocations([]);
		}
	}, [currentClasses]);

	function chooseLocation(location: LocationItem) {
		if (multiMode) {
			const selectedId = getLocationId(location);
			const selected = choosedLocations.some((item) => getLocationId(item) === selectedId);
			if (selected) {
				setChoosedLocations(choosedLocations.filter((item) => getLocationId(item) !== selectedId));
			} else {
				setChoosedLocations([...choosedLocations, location]);
			}
		} else {
			setChoosedLocations([location]);
		}
	}

	function switchDropped() {
		setShowDeleted(!showDeleted);
	}

	function updateKeyword(e: ChangeEvent<HTMLInputElement>) {
		setKeyword(e.target.value);
	}

	function swtichAllCheck() {
		setShowAllCheck(!showAllCheck);
	}

	async function changeClassLocationLink(location: LocationItem) {
		try {
			if (!currentClasses || currentClasses.length === 0) {
				return;
			}

			const currentClass = currentClasses[0];
			const locationId = getLocationId(location);
			if (!locationId) {
				return;
			}

			const classClientId = currentClass.client_id;
			if (!classClientId) {
				return;
			}

			const copyCurrentClass: ClientClass = {
				...currentClass,
				location_links: [...(currentClass.location_links || [])],
			};

			if (!existLocations.includes(locationId)) {
				setExistLocations([...existLocations, locationId]);
				const link = await LocationService.linkLocationClass(classClientId, locationId, currentClass.id);
				copyCurrentClass.location_links = [...(copyCurrentClass.location_links || []), link];
			} else {
				setExistLocations(existLocations.filter((id) => id !== locationId));
				const link = await LocationService.unlinkLocationClass(classClientId, locationId, currentClass.id);
				copyCurrentClass.location_links = (currentClass.location_links || []).filter((item: LocationLink) => {
					return String(item.locationclassid) !== String(link.locationclassid);
				});
			}

			if (onCurrentClassChange) {
				onCurrentClassChange(copyCurrentClass);
			}
		} catch (e) {
		}
	}

	let filteredLocations: LocationItem[] = [];
	if (locations && locations.length) {
		const normalizedKeyword = keyword.trim().toLowerCase();
		filteredLocations = (locations as LocationItem[]).filter((item) => {
			const name = getLocationName(item);
			if (item.drop_date && item.drop_date !== '') {
				return false;
			}

			if (normalizedKeyword && name.toLowerCase().indexOf(normalizedKeyword) === -1) {
				return false;
			}

			if (showDeleted) {
				return true;
			}
			return !item.deleted_at;
		});

		if (!showAllCheck && assignAvailable) {
			filteredLocations = filteredLocations.filter((item) => {
				const locationId = getLocationId(item);
				return locationId ? existLocations.includes(locationId) : false;
			});
		}
	}

	return <Box sx={jss.dataGridItem}>
		<Box sx={jss.dataTop}>
			<Box sx={jss.dataTitle}>
				Locations
				{onMultiModeChange &&
					<FormControlLabel
						control={<Switch value={multiMode} size="small" onChange={onMultiModeChange} />}
						label="Multi select"
						sx={{ ml: 2 }}
					/>
				}
			</Box>
		</Box>
		<Box sx={jss.actionSection}>
			<TextField
				size="small"
				variant="outlined"
				placeholder="Search"
				value={keyword}
				onChange={updateKeyword}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<SearchIcon />
						</InputAdornment>
					),
				}}
			/>
			<Box sx={jss.dataAction}>
				<IconButton size="small" onClick={e => setOpenFilter(true)} ref={filterElement}><FilterListRoundedIcon /></IconButton>
				{!multiMode && assignAvailable && isAdmin ?
					<IconButton size="small" sx={showAllCheck ? { ...jss.iconButtonActive } : {}} onClick={swtichAllCheck}><PlaylistAddCheckIcon /></IconButton>
				: null}
				<Menu
					open={openFilter}
					anchorEl={filterElement.current}
					onClose={e => { setOpenFilter(false) }}
				>
					<MenuItem sx={{ width: 200 }} dense onClick={switchDropped}>
						<ListItemIcon>{showDeleted && <Check />}</ListItemIcon>
						<ListItemText>Dropped</ListItemText>
					</MenuItem>
				</Menu>
			</Box>
		</Box>
		{!locations &&
			<Box sx={jss.loadingSection}>
				<CircularProgress />
			</Box>
		}
		{(locations && (!assignAvailable || currentClasses.length > 0)) &&
			<Box sx={jss.dataContent}>
				{filteredLocations.map((_location) => {
					const locationId = getLocationId(_location);
					let className = '';
					if (choosedLocations.some((item) => getLocationId(item) === locationId)) {
						className += ' _active ';
					}
					if (_location.deleted_at) {
						className += ' _deleted';
					}
					if (locationId && existLocationClasses?.[locationId]?.isOverride) {
						className += ' _override';
					}
					return (
						<Box
							sx={jss.dataContentItem}
							className={className}
							key={`location_item_${locationId || getLocationName(_location)}`}
						>
							{showAllCheck &&
								<Checkbox
									size="small"
									checked={locationId ? existLocations.includes(locationId) : false}
									onChange={e => changeClassLocationLink(_location)}
								/>
							}
							<Box sx={jss.dataContentItem.name} onClick={e => chooseLocation(_location)}>
								{getLocationName(_location)}
							</Box>
							<Box sx={jss.dataContentItem.action} onClick={e => chooseLocation(_location)}>
								{getLocationCode(_location)}
								{locationId && existLocationClasses?.[locationId]?.service &&
									<Chip label={existLocationClasses[locationId].service} size="small" />
								}
							</Box>
						</Box>
					);
				})}
				{assignAvailable && (!filteredLocations || filteredLocations.length === 0) &&
					<Box sx={jss.dataContent.empty}>
						<Box>There are no locations for the selected class.</Box>
						<Box>You can select locations for this class.</Box>
					</Box>
				}
			</Box>
		}
	</Box>;
}

export default ClientProfileLocationList;
